const chapter = {
  id: 'chemical-coordination-integration',
  slug: 'chemical-coordination-integration',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Chemical Coordination and Integration',
        hindi: 'रासायनिक समन्वय और एकीकरण',
  },
  estReadMinutes: 60,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '3-4 questions in NEET',
        hindi: 'NEET में 3-4 प्रश्न',
  },
  syllabusTags: ['Human Physiology', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'endocrine-overview',
      title: {
        english: 'Endocrine System Overview',
        hindi: 'अन्तःस्रावी तंत्र अवलोकन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Hormones = non-nutrient chemicals secreted in trace amounts; intercellular messengers; ductless glands secrete into blood.',
        hindi: 'Hormones = non-nutrient chemicals secreted in trace amounts; अंतरकोशिकीय दूत; नलिकाहीन ग्रंथियाँ रक्त में स्रावित करती हैं।',
            },
            {
              english: 'Endocrine glands: **hypothalamus, pituitary, pineal, thyroid, parathyroid, thymus, adrenal, pancreas, gonads (testis, ovary)** + GI tract, kidney, heart.',
        hindi: 'अंतःस्रावी ग्रंथियां: **हाइपोथैलेमस, पिट्यूटरी, पीनियल, थायरॉयड, पैराथायराइड, थाइमस, अधिवृक्क, अग्न्याशय, गोनाड (वृषण, अंडाशय)** + जीआई पथ, गुर्दे, हृदय।',
            },
            {
              english: 'Hormones chemically: peptide/protein (insulin, GH), steroid (cortisol, sex hormones), iodothyronine (T3, T4), amino-acid derivative (adrenaline).',
        hindi: 'हार्मोन रासायनिक रूप से: peptide/protein (इंसुलिन, GH), स्टेरॉयड (कोर्टिसोल, सेक्स हार्मोन), आयोडोथायरोनिन (T3, T4), अमीनो-एसिड व्युत्पन्न (एड्रेनालाईन)।',
            },
          ],
        },
      ],
    },
    {
      id: 'hypothalamus-pituitary',
      title: {
        english: 'Hypothalamus & Pituitary',
        hindi: 'हाइपोथैलेमस और पिट्यूटरी',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Hypothalamus** — base of forebrain; secretes **releasing (e.g. GnRH)** + **inhibiting hormones** that control anterior pituitary.',
        hindi: '**हाइपोथैलेमस** — अग्रमस्तिष्क का आधार; स्राव **विमोचन (जैसे जीएनआरएच)** + **अवरोधक हार्मोन** जो पूर्वकाल पिट्यूटरी को नियंत्रित करते हैं।',
            },
            {
              english: '**Pituitary (hypophysis)** — in **sella turcica** of sphenoid; "**master gland**" — but itself controlled by hypothalamus.',
        hindi: '**पिट्यूटरी (हाइपोफिसिस)** - स्फेनॉइड के **सेला टरिका** में; "**मास्टर ग्रंथि**" - लेकिन स्वयं हाइपोथैलेमस द्वारा नियंत्रित होती है।',
            },
            {
              english: '**Anterior pituitary (adenohypophysis)** secretes:',
        hindi: '**पूर्ववर्ती पिट्यूटरी (एडेनोहाइपोफिसिस)** स्रावित करता है:',
            },
            {
              english: '• **GH** — somatic growth; deficiency in childhood = **dwarfism**; excess = **gigantism (childhood) / acromegaly (adult)**.',
        hindi: '• **जीएच** - दैहिक विकास; बचपन में कमी = **बौनापन**; अतिरिक्त = **विशालता (बचपन) / एक्रोमेगाली (वयस्क)**।',
            },
            {
              english: '• **PRL (Prolactin)** — milk production.',
        hindi: '• **पीआरएल (प्रोलैक्टिन)** — दूध उत्पादन।',
            },
            {
              english: '• **TSH** — stimulates thyroid (T3, T4).',
        hindi: '• **टीएसएच** - थायराइड (टी3, टी4) को उत्तेजित करता है।',
            },
            {
              english: '• **ACTH** — stimulates adrenal cortex (glucocorticoids).',
        hindi: '• **एसीटीएच** - अधिवृक्क प्रांतस्था (ग्लुकोकोर्टिकोइड्स) को उत्तेजित करता है।',
            },
            {
              english: '• **LH, FSH** — gonadotropins (gonad development + sex hormones).',
        hindi: '• **एलएच, एफएसएच** - गोनाडोट्रोपिन (गोनाड विकास + सेक्स हार्मोन)।',
            },
            {
              english: '• **MSH** — melanocyte (pigmentation; in pars intermedia).',
        hindi: '• **एमएसएच** - मेलानोसाइट (पिग्मेंटेशन; पार्स इंटरमीडिया में)।',
            },
            {
              english: '**Posterior pituitary (neurohypophysis)** — only stores; releases **oxytocin + vasopressin (ADH)** synthesised by hypothalamus.',
        hindi: '**पश्च पिट्यूटरी (न्यूरोहाइपोफिसिस)** - केवल भंडार; हाइपोथैलेमस द्वारा संश्लेषित **ऑक्सीटोसिन + वैसोप्रेसिन (एडीएच)** जारी करता है।',
            },
            {
              english: '• **Oxytocin** — uterine contraction at parturition; milk ejection.',
        hindi: '• **ऑक्सीटोसिन** — प्रसव के समय गर्भाशय संकुचन; दूध का निष्कासन.',
            },
            {
              english: '• **ADH** — water reabsorption from DCT/CT of kidney; deficiency → **diabetes insipidus**.',
        hindi: '• **एडीएच** - गुर्दे के DCT/CT से पानी का पुनर्अवशोषण; कमी → **डायबिटीज इन्सिपिडस**।',
            },
          ],
        },
      ],
    },
    {
      id: 'pineal-thyroid-parathyroid',
      title: {
        english: 'Pineal · Thyroid · Parathyroid',
        hindi: 'पीनियल · थायराइड · पैराथायराइड',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Pineal** (dorsal forebrain) — **melatonin** → regulates **circadian rhythm, sleep-wake cycle, body temperature, pigmentation, menstrual cycle, defence**.',
        hindi: '**पीनियल** (पृष्ठीय अग्रमस्तिष्क) - **मेलाटोनिन** → **सर्कैडियन लय, नींद-जागने का चक्र, शरीर का तापमान, रंजकता, मासिक धर्म चक्र, रक्षा** को नियंत्रित करता है।',
            },
            {
              english: '**Thyroid** — bilobed, joined by **isthmus**; follicles secrete iodine-containing hormones. Need **iodine** for synthesis.',
        hindi: '**थायराइड** - द्विपालीय, **इस्थमस** से जुड़ा हुआ; रोम आयोडीन युक्त हार्मोन स्रावित करते हैं। संश्लेषण के लिए **आयोडीन** की आवश्यकता है।',
            },
            {
              english: '• **T₃ (triiodothyronine), T₄ (tetraiodothyronine = thyroxine)** — regulate **BMR**, RBC formation, metabolism of carbohydrates, proteins, fats; help maintain water-electrolyte balance.',
        hindi: '• **T₃ (ट्राईआयोडोथायरोनिन), T₄ (tetraiodothyronine = thyroxine)** — regulate **BMR**, RBC गठन, कार्बोहाइड्रेट, प्रोटीन, वसा का चयापचय; जल-इलेक्ट्रोलाइट संतुलन बनाए रखने में मदद करता है।',
            },
            {
              english: '• Deficiency: **Goitre** (enlarged thyroid; iodine-deficient diet); **Cretinism** (in infants — stunted growth, mental retardation, deaf-mutism); **Hypothyroidism** in pregnant mother → defective foetal development.',
        hindi: '• कमी: **घेंघा** (बढ़ा हुआ थायराइड; आयोडीन की कमी वाला आहार); **क्रेटिनिज़्म** (शिशुओं में - अवरुद्ध विकास, मानसिक मंदता, बहरा-गूंगापन); **गर्भवती माँ में हाइपोथायरायडिज्म** → दोषपूर्ण भ्रूण विकास।',
            },
            {
              english: '• **Hyperthyroidism** (e.g. cancer/nodules) — ↑ BMR, weight loss, exophthalmic goitre (Grave\'s disease).',
        hindi: '• **हाइपरथायरायडिज्म** (जैसे cancer/nodules) - ↑ बीएमआर, वजन घटना, एक्सोफथैल्मिक गोइटर (ग्रेव्स रोग)।',
            },
            {
              english: '• **Calcitonin** — secreted by C-cells of thyroid; **lowers blood Ca²⁺** by depositing in bones.',
        hindi: '• **कैल्सीटोनिन** — थायरॉयड की सी-कोशिकाओं द्वारा स्रावित; **हड्डियों में जमा होकर रक्त Ca²⁺** को कम करता है। **पैराथाइरॉइड** - थायरॉयड के पीछे 4 छोटी ग्रंथियां; **पीटीएच (पैराथोर्मोन)** - **हाइपरकैल्सीमिक** हार्मोन का स्राव करें:',
            },
            {
              english: '**Parathyroid** — 4 small glands on back of thyroid; secrete **PTH (Parathormone)** — **hypercalcemic** hormone:',
        hindi: '**पैराथाइरॉइड** - थायरॉयड के पीछे 4 छोटी ग्रंथियाँ; **पीटीएच (पैराथोर्मोन)** - **हाइपरकैल्सीमिक** हार्मोन का स्राव करें:',
            },
            {
              english: '• ↑ blood Ca²⁺ by bone resorption (acts on osteoclasts), ↑ Ca²⁺ reabsorption from kidney, ↑ Ca²⁺ absorption from intestine (via vit-D activation).',
        hindi: '• ↑ रक्त Ca²⁺ हड्डी पुनर्शोषण द्वारा (ऑस्टियोक्लास्ट पर कार्य करता है), ↑ Ca²⁺ गुर्दे से पुनर्अवशोषण, ↑ Ca²⁺ आंत से अवशोषण (vit-D सक्रियण के माध्यम से)।',
            },
            {
              english: '• PTH + Calcitonin = antagonists for Ca²⁺ homeostasis.',
        hindi: '• PTH + Calcitonin = antagonists for Ca²⁺ homeostasis। **थाइमस** - फेफड़ों के बीच, उरोस्थि के पीछे; बच्चों में बड़ा, **उम्र के साथ ख़राब हो जाता है** (बुढ़ापे में कम प्रतिरक्षा)।',
            },
          ],
        },
      ],
    },
    {
      id: 'thymus-adrenal',
      title: {
        english: 'Thymus & Adrenal',
        hindi: 'थाइमस और अधिवृक्क',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Thymus** — between lungs, behind sternum; large in children, **degenerates with age** (less immunity in old age).',
        hindi: '**थाइमस** — फेफड़ों के बीच, उरोस्थि के पीछे; बच्चों में बड़ा, **उम्र के साथ ख़राब हो जाता है** (बुढ़ापे में कम प्रतिरक्षा)।',
            },
            {
              english: '• **Thymosins** — differentiation of **T-lymphocytes** → cell-mediated immunity; promote production of antibodies.',
        hindi: '• **थाइमोसिन** — **टी-लिम्फोसाइट्स** का विभेदन → कोशिका-मध्यस्थ प्रतिरक्षा; एंटीबॉडी के उत्पादन को बढ़ावा देना।',
            },
            {
              english: '**Adrenal** — paired glands above kidneys; **cortex (outer) + medulla (inner)**.',
        hindi: '**एड्रेनल** - गुर्दे के ऊपर युग्मित ग्रंथियाँ; **कॉर्टेक्स (बाहरी) + मेडुला (आंतरिक)**।',
            },
            {
              english: '**Adrenal medulla** — secretes **adrenaline (epinephrine) + noradrenaline (norepinephrine)** = **catecholamines**; "**emergency / fight-flight / 3F (Fight, Flight, Fright)**" hormones — ↑ HR, BP, breathing, alertness, pupil dilation, piloerection, glycogenolysis.',
        hindi: '**एड्रेनल मेडुला** - स्राव **एड्रेनालाईन (एपिनेफ्रिन) + नॉरएड्रेनालाईन (नॉरपेनेफ्रिन)** = **कैटेकोलामाइन्स**; "**आपातकाल / लड़ाई-उड़ान / 3एफ (लड़ाई, उड़ान, डर)**" हार्मोन - ↑ एचआर, बीपी, श्वास, सतर्कता, पुतली का फैलाव, पाइलोएरेक्शन, ग्लाइकोजेनोलिसिस।',
            },
            {
              english: '**Adrenal cortex** — 3 layers; secretes **corticoids**:',
        hindi: '**अधिवृक्क प्रांतस्था** — 3 परतें; स्राव **कॉर्टिकोइड्स**:',
            },
            {
              english: '• **Glucocorticoids (cortisol)** — gluconeogenesis, lipolysis, proteolysis; suppress immune response; anti-inflammatory; ↑ RBC.',
        hindi: '• **ग्लूकोकोर्टिकोइड्स (कोर्टिसोल)** - ग्लूकोनियोजेनेसिस, लिपोलिसिस, प्रोटियोलिसिस; प्रतिरक्षा प्रतिक्रिया को दबाना; सूजनरोधी; ↑ आरबीसी।',
            },
            {
              english: '• **Mineralocorticoids (aldosterone)** — Na⁺ + water reabsorption from DCT of kidney; ↑ BP.',
        hindi: '• **मिनरलोकॉर्टिकोइड्स (एल्डोस्टेरोन)** - Na⁺ + गुर्दे की DCT से पानी का पुनर्अवशोषण; ↑ बी.पी.',
            },
            {
              english: '• **Sex corticoids** — small amounts of androgens.',
        hindi: '• **सेक्स कॉर्टिकोइड्स** - एण्ड्रोजन की थोड़ी मात्रा।',
            },
            {
              english: 'Hypoactivity (Addison\'s disease) — low aldosterone + cortisol; hyperactivity (Cushing\'s syndrome) — moon face, hypertension.',
        hindi: 'हाइपोएक्टिविटी (एडिसन रोग) - कम एल्डोस्टेरोन + कोर्टिसोल; अतिसक्रियता (कुशिंग सिंड्रोम) - चाँद जैसा चेहरा, उच्च रक्तचाप।',
            },
          ],
        },
      ],
    },
    {
      id: 'pancreas-gonads',
      title: {
        english: 'Pancreas & Gonads',
        hindi: 'अग्न्याशय और जनदग्रन्थि',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Pancreas** — composite; exocrine (digestive juice) + endocrine (**Islets of Langerhans**, ~ 1-2 million). Cell types:',
        hindi: '**अग्न्याशय** — समग्र; एक्सोक्राइन (पाचन रस) + एंडोक्राइन (**लैंगरहैंस के आइलेट्स**, ~ 1-2 मिलियन)। कोशिका प्रकार:',
            },
            {
              english: '• **α-cells** → **glucagon** (hyperglycemic; glycogenolysis + gluconeogenesis in liver).',
        hindi: '• **α-कोशिकाएं** → **ग्लूकागन** (हाइपरग्लाइसेमिक; ग्लाइकोजेनोलिसिस + यकृत में ग्लूकोनियोजेनेसिस)।',
            },
            {
              english: '• **β-cells** → **insulin** (hypoglycemic; glucose uptake by cells, glycogenesis). Deficiency → **diabetes mellitus** (hyperglycemia, glycosuria, ketoacidosis); managed by insulin therapy.',
        hindi: '• **बीटा-कोशिकाएं** → **इंसुलिन** (हाइपोग्लाइसेमिक; कोशिकाओं द्वारा ग्लूकोज ग्रहण, ग्लाइकोजेनेसिस)। कमी → **मधुमेह मेलेटस** (हाइपरग्लेसेमिया, ग्लाइकोसुरिया, कीटोएसिडोसिस); इंसुलिन थेरेपी द्वारा प्रबंधित।',
            },
            {
              english: '**Testis (male gonad)** — Leydig (interstitial) cells secrete **androgens (testosterone)** under LH; → male accessory ducts/glands development, secondary sex characters (beard, deep voice), spermatogenesis, anabolic effects on muscle/bone.',
        hindi: '**वृषण (पुरुष गोनाड)** - लेडिग (अंतरालीय) कोशिकाएं एलएच के तहत **एण्ड्रोजन (टेस्टोस्टेरोन)** का स्राव करती हैं; → पुरुष सहायक ducts/glands विकास, द्वितीयक यौन लक्षण (दाढ़ी, गहरी आवाज), शुक्राणुजनन, muscle/bone पर उपचय प्रभाव।',
            },
            {
              english: '**Ovary (female gonad)** — secretes **estrogen** (Graafian follicle; secondary sex characters, mammary gland) + **progesterone** (corpus luteum; maintains pregnancy, mammary alveoli).',
        hindi: '**अंडाशय (मादा गोनाड)** - **एस्ट्रोजन** (ग्राफियन फॉलिकल; द्वितीयक यौन लक्षण, स्तन ग्रंथि) + **प्रोजेस्टेरोन** (कॉर्पस ल्यूटियम; गर्भावस्था, स्तन एल्वियोली को बनाए रखता है) स्रावित करता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'other-hormones-mechanism',
      title: {
        english: 'Other Hormones & Mechanism of Action',
        hindi: 'अन्य हार्मोन और क्रिया की क्रियाविधि',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Other endocrine sites:',
        hindi: 'अन्य अंतःस्रावी साइटें:',
            },
            {
              english: '• **Heart (atria)** — **Atrial Natriuretic Factor (ANF)** → vasodilation, ↓ BP.',
        hindi: '• **हृदय (एट्रिया)** — **एट्रियल नैट्रियूरेटिक फैक्टर (एएनएफ)** → वासोडिलेशन, ↓ बीपी।',
            },
            {
              english: '• **Kidney** — **Erythropoietin** (peritubular cells) → RBC formation; **Renin** → angiotensin II → aldosterone (↑ BP).',
        hindi: '• **गुर्दा** — **एरिथ्रोपोइटिन** (पेरीटुबुलर कोशिकाएं) → आरबीसी गठन; **रेनिन** → एंजियोटेंसिन II → एल्डोस्टेरोन (↑ बीपी)।',
            },
            {
              english: '• **Gastrointestinal tract** — Gastrin (HCl + pepsinogen), Secretin (pancreatic bicarbonate + water), CCK (pancreatic enzymes + bile), GIP (inhibits gastric).',
        hindi: '• **गैस्ट्रोइंटेस्टाइनल ट्रैक्ट** - गैस्ट्रिन (एचसीएल + पेप्सिनोजन), सेक्रेटिन (अग्नाशय बाइकार्बोनेट + पानी), सीसीके (अग्नाशय एंजाइम + पित्त), जीआईपी (गैस्ट्रिक को रोकता है)।',
            },
            {
              english: '**Mechanism of hormone action**:',
        hindi: '**हार्मोन क्रिया का तंत्र**:',
            },
            {
              english: '• **Lipid-soluble (steroid, thyroid)** — diffuse through membrane → bind **intracellular receptors** → hormone-receptor complex → DNA → ↑ gene expression.',
        hindi: '• **लिपिड-घुलनशील (स्टेरॉयड, थायरॉयड)** - झिल्ली के माध्यम से फैलाना → बांधना **इंट्रासेल्युलर रिसेप्टर्स** → हार्मोन-रिसेप्टर कॉम्प्लेक्स → डीएनए → ↑ जीन अभिव्यक्ति।',
            },
            {
              english: '• **Water-soluble (peptide, protein, catecholamines)** — bind **membrane receptors** → activate G-protein/adenylyl cyclase → **second messenger (cAMP, IP₃, Ca²⁺)** → enzyme cascade.',
        hindi: '• **पानी में घुलनशील (पेप्टाइड, प्रोटीन, कैटेकोलामाइन)** - **झिल्ली रिसेप्टर्स को बांधें** → G-protein/adenylyl साइक्लेज़ को सक्रिय करें → **दूसरा मैसेंजर (cAMP, IP₃, Ca²⁺)** → एंजाइम कैस्केड।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Pituitary in sella turcica; controlled by hypothalamus}',
      desc: {
        english: '',
        hindi: 'सेला टरिका में पिट्यूटरी; हाइपोथैलेमस द्वारा नियंत्रित',
      },
    },
    {
      latex: '\\text{GH excess: gigantism (child) / acromegaly (adult)}',
      desc: {
        english: '',
        hindi: 'जीएच की अधिकता: विशालता (बच्चा) / एक्रोमेगाली (वयस्क)',
      },
    },
    {
      latex: '\\text{ADH deficiency} \\to \\text{diabetes insipidus}',
      desc: {
        english: '',
        hindi: 'एडीएच की कमी से डायबिटीज इन्सिपिडस',
      },
    },
    {
      latex: '\\text{Calcitonin} \\downarrow \\text{Ca}^{2+}; \\text{PTH} \\uparrow \\text{Ca}^{2+} \\text{(antagonists)}',
      desc: {
        english: '',
        hindi: 'कैल्सीटोनिन सीए 2+ ; पीटीएच सीए 2+ (प्रतिपक्षी)',
      },
    },
    {
      latex: '\\text{Adrenal medulla} \\to \\text{adrenaline (3F: Fight, Flight, Fright)}',
      desc: {
        english: '',
        hindi: 'अधिवृक्क मज्जा एड्रेनालाईन (3एफ: लड़ाई, उड़ान, डर)',
      },
    },
    {
      latex: '\\beta\\text{-cells} \\to \\text{insulin}; \\alpha\\text{-cells} \\to \\text{glucagon}',
      desc: {
        english: '',
        hindi: '-कोशिकाएं इंसुलिन; -कोशिकाएं ग्लूकागन',
      },
    },
    {
      latex: '\\text{Steroid hormone} \\to \\text{intracellular receptor; Peptide} \\to \\text{cAMP 2nd messenger}',
      desc: {
        english: '',
        hindi: 'स्टेरॉयड हार्मोन इंट्रासेल्युलर रिसेप्टर; पेप्टाइड कैंप दूसरा संदेशवाहक',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Pancreas',
      problem: {
        english: 'Hormone secreted by α-cells of Islets of Langerhans is:',
        hindi: 'लैंगरहैंस के आइलेट्स की α-कोशिकाओं द्वारा स्रावित हार्मोन है:',
      },
      answer: {
        english: 'Glucagon',
        hindi: 'ग्लूकागन',
      },
      solution: {
        english: 'Hyperglycemic; opposite of insulin.',
        hindi: 'हाइपरग्लेसेमिक; इंसुलिन के विपरीत.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Adrenal',
      problem: {
        english: 'Hormone responsible for "fight-or-flight" response:',
        hindi: '"लड़ो-या-उड़ाओ" प्रतिक्रिया के लिए जिम्मेदार हार्मोन:',
      },
      answer: {
        english: 'Adrenaline (epinephrine)',
        hindi: 'एड्रेनालाईन (एपिनेफ्रिन)',
      },
      solution: {
        english: 'Adrenal medulla.',
        hindi: 'अधिवृक्क मज्जा।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Calcium',
      problem: {
        english: 'Calcitonin and PTH are antagonistic for:',
        hindi: 'कैल्सीटोनिन और पीटीएच इसके लिए विरोधी हैं:',
      },
      answer: {
        english: 'Blood calcium homeostasis',
        hindi: 'रक्त कैल्शियम होमियोस्टैसिस',
      },
      solution: {
        english: 'PTH ↑ Ca²⁺; Calcitonin ↓ Ca²⁺.',
        hindi: 'पीटीएच ↑ Ca²⁺; कैल्सीटोनिन ↓ Ca²⁺.',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Pituitary',
      problem: {
        english: 'Excess of GH in adults causes:',
        hindi: 'वयस्कों में GH की अधिकता के कारण:',
      },
      answer: {
        english: 'Acromegaly',
        hindi: 'एक्रोमेगाली',
      },
      solution: {
        english: 'Childhood excess → gigantism.',
        hindi: 'बचपन की अधिकता → विशालता।',
      },
    },
    {
      year: 2018,
      exam: 'NEET',
      tag: 'Pineal',
      problem: {
        english: 'Melatonin is secreted by:',
        hindi: 'मेलाटोनिन स्रावित होता है:',
      },
      answer: {
        english: 'Pineal gland',
        hindi: 'पीनियल ग्रंथि',
      },
      solution: {
        english: 'Regulates circadian/sleep-wake cycle.',
        hindi: 'circadian/sleep-जागृति चक्र को नियंत्रित करता है।',
      },
    },
  ],
  next: {
    slug: 'biomolecules-class11',
    title: 'Biomolecules (Class 11)',
  },
};

export default chapter;
